import type { RefundResult } from '../api/client'
import type { PaymentRecord } from '../types/app'
import { formatPrice, parsePrice } from '../catalog/format'

// refundsToCard is whether a refund of the payment goes back to a card
// through Stripe, which takes 5–10 days to arrive. Stripe's method is
// unknown on older rows, all of them cards; Klarna and the rest differ.
export function refundsToCard(p: Pick<PaymentRecord, 'through_stripe' | 'stripe_method'>): boolean {
  return p.through_stripe && (!p.stripe_method || p.stripe_method === 'card')
}

// suggestedPackageRefund is the per-session price (rounded down to a penny)
// for each credit removed. It is only a suggestion; she can edit the amount.
export function suggestedPackageRefund(pricePaidPence: number, sessionsTotal: number, credits: number): number {
  if (sessionsTotal <= 0 || credits <= 0) return 0
  return Math.floor(pricePaidPence / sessionsTotal) * credits
}

// refundable is what can still be given back after earlier refunds.
export function refundable(paidPence: number, refundedPence: number): number {
  return Math.max(0, paidPence - refundedPence)
}

// amountProblem checks the typed amount (pounds); null means it is fine.
export function amountProblem(input: string, maxPence: number): string | null {
  const pence = parsePrice(input)
  if (pence === null || pence <= 0) return 'Enter an amount to refund.'
  if (pence > maxPence) return `That’s more than can be refunded. The most is ${formatPrice(maxPence)}.`
  return null
}

export interface RefundDetails {
  amount_pence: number
  remove_credits: number
  reason: string | null
}

export interface RefundAttempt {
  key: string
  details: RefundDetails
  // True once the server said this refund definitely failed (502
  // refund-failed). That answer is final for the key, so it is never reused.
  failed?: boolean
}

// keyForAttempt picks the idempotency key for a refund attempt. A retry of
// the same details reuses the previous key (so a lost answer can't refund
// twice); a key whose refund definitely failed is never reused, because the
// server replays that failure for the same key; changing the amount, credits or reason needs a new key, because the
// server refuses a key reused with different details.
export function keyForAttempt(
  previous: RefundAttempt | null,
  details: RefundDetails,
  generate: () => string,
): RefundAttempt {
  if (
    previous &&
    !previous.failed &&
    previous.details.amount_pence === details.amount_pence &&
    previous.details.remove_credits === details.remove_credits &&
    previous.details.reason === details.reason
  ) {
    return previous
  }
  return { key: generate(), details }
}

export type RefundOutcome = { kind: 'done' | 'pending' | 'failed'; message: string }

// refundOutcome words the server's answer to a refund that was accepted.
export function refundOutcome(result: RefundResult): RefundOutcome {
  if (result.status === 'pending') {
    return { kind: 'pending', message: "Refund pending — we’ll update this when it’s confirmed" }
  }
  if (result.status === 'failed') {
    return { kind: 'failed', message: "The refund didn’t go through. Nothing was refunded." }
  }
  return { kind: 'done', message: `Refunded ${formatPrice(result.amount_pence)}` }
}
